import { datadogLogs } from "@datadog/browser-logs";
import { getSessionAuth } from "@redotech/redo-merchant-app-common/auth";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { getStoreScopedPath } from "@redotech/redo-merchant-app-common/routing/store-scope";
import type { LoaderFunction, Params, RouteObject } from "react-router-dom";
import { matchRoutes, redirect } from "react-router-dom";
import { storePaths, type StorePaths } from "../../../redo-suite/store-paths";
import { csatSubpage } from "../../../setting/customer-satisfaction/csat-deep-links";

export const routerBasename: string | undefined = (() => {
  if (globalThis.REDO_MOCK_MODE !== "true" || typeof document === "undefined") {
    return undefined;
  }
  const path = new URL(document.baseURI).pathname
    .replace(/\/vite\.html$/, "")
    .replace(/\/$/, "");
  return path === "" ? undefined : path;
})();

interface RedirectTarget {
  /**
   * `search` is a mutable copy of the incoming query; whatever remains is appended.
   * `pathname` is the matched part of the URL and `tail` the rest of a splat, both
   * still percent-encoded exactly as requested and without the basename.
   */
  to(args: {
    params: Params;
    search: URLSearchParams;
    paths: StorePaths;
    pathname: string;
    tail: string;
  }): string;
}

export interface LegacyRedirect extends RedirectTarget {
  /** React Router pattern relative to the store root, e.g. "order-management/returns/*". */
  from: string;
}

function appendTail(base: string, tail: string): string {
  return tail ? `${base}/${tail}` : base;
}

function redirectWithTail(
  from: string,
  destination: (paths: StorePaths) => string,
): LegacyRedirect {
  return {
    from,
    to({ paths, tail }) {
      return appendTail(destination(paths), tail);
    },
  };
}

function redirectExact(
  from: string,
  destination: (paths: StorePaths) => string,
): LegacyRedirect {
  return {
    from,
    to({ paths }) {
      return destination(paths);
    },
  };
}

function segment(params: Params, key: string): string {
  return encodeURIComponent(params[key] ?? "");
}

/** Return-list URLs the old top-level `/returns` splat forwarded. */
function legacyReturnListRedirects(): LegacyRedirect[] {
  const toList =
    (tail: (params: Params) => string): LegacyRedirect["to"] =>
    ({ paths, params }) =>
      `${paths.returns}/returns/${tail(params)}`;
  return [
    redirectExact(
      "returns/all-returns",
      (paths) => `${paths.returns}/returns/all-returns`,
    ),
    {
      from: "returns/all-returns/:returnId",
      to: toList((params) => `all-returns/${segment(params, "returnId")}`),
    },
    {
      from: "returns/create-return/:orderId",
      to: toList((params) => `create-return/${segment(params, "orderId")}`),
    },
    redirectExact(
      "returns/create-third-party-return",
      (paths) => `${paths.returns}/returns/create-third-party-return`,
    ),
    redirectExact(
      "returns/instant-exchange",
      (paths) => `${paths.returns}/returns/instant-exchange`,
    ),
    redirectExact(
      "returns/gift-receipt-requests",
      (paths) => `${paths.returns}/returns/gift-receipt-requests`,
    ),
    redirectExact(
      "returns/inmar-blind-returns",
      (paths) => `${paths.returns}/returns/inmar-blind-returns`,
    ),
    {
      from: "returns/:viewName",
      to: toList((params) => segment(params, "viewName")),
    },
    {
      from: "returns/:viewName/:returnId",
      to: toList(
        (params) =>
          `${segment(params, "viewName")}/${segment(params, "returnId")}`,
      ),
    },
  ];
}

function analyticsRedirects(prefix: string): LegacyRedirect[] {
  const marketingAnalytics = (paths: StorePaths) =>
    `${paths.marketing}/analytics`;
  return [
    redirectExact(prefix, marketingAnalytics),
    redirectWithTail(
      `${prefix}/returns-claims/*`,
      (paths) => `${paths.returns}/analytics/returns-claims`,
    ),
    redirectExact(
      `${prefix}/reports`,
      (paths) => `${paths.returns}/analytics/reports`,
    ),
    redirectExact(
      `${prefix}/order-tracking`,
      (paths) => `${paths.marketing}/analytics/order-tracking`,
    ),
    redirectExact(`${prefix}/marketing`, marketingAnalytics),
    redirectExact(
      `${prefix}/try-first`,
      (paths) => `${paths.conversion}/analytics`,
    ),
    redirectWithTail(`${prefix}/*`, marketingAnalytics),
  ];
}

const packingSlips = (paths: StorePaths) =>
  `${paths.settings}/oms/printing/packing-slips`;

const LEGACY_INTEGRATION_SLUGS = [
  "comment-sold",
  "netsuite",
  "indigo8",
  "odoo",
  "shipmonk",
  "masonhub",
  "trackstar",
  "aims360",
  "apparel-magic",
  "authorizenet",
  "blue-cherry",
  "controlport",
  "mintsoft",
  "blue-yonder",
  "brightpearl",
  "cloudx",
  "bubblehouse",
  "creditsyard",
  "custom-return",
  "aptos",
  "deposco",
  "extensiv",
  "flexport",
  "globale",
  "idf-shipping",
  "ecwid",
  "inmar",
  "katana",
  "klaviyo",
  "loop",
  "narvar",
  "return-logic",
  "luminous",
  "purple-dot",
  "refundid",
  "rise-ai",
  "loopz",
  "rivo",
  "seko",
  "shipbob",
  "shipedge",
  "shiphero",
  "ryder",
  "cin7-omni",
  "teamwork-commerce",
  "tiktok-shop",
];

/** Per-vendor settings pages that now live on the unified integrations hub. */
function legacyIntegrationVendorRedirects(): LegacyRedirect[] {
  const vendor = (slug: string) => (paths: StorePaths) =>
    `${paths.settings}/integrations/vendor/${slug}`;
  return [
    ...LEGACY_INTEGRATION_SLUGS.map((slug) =>
      redirectExact(`settings/integrations/${slug}`, vendor(slug)),
    ),
    redirectExact(
      "settings/integrations/comment-sold/manage",
      vendor("comment-sold"),
    ),
  ];
}

/** Legacy per-product integrations pages, pre-filtered to a hub product when given. */
function hubRedirect(from: string, products?: string): LegacyRedirect {
  return {
    from,
    to({ paths, search }) {
      if (products) {
        search.set("products", products);
      }
      return `${paths.settings}/integrations`;
    },
  };
}

export const LEGACY_REDIRECTS: readonly LegacyRedirect[] = [
  redirectWithTail("home/popups/*", (paths) => `${paths.conversion}/popups`),
  redirectWithTail(
    "home/spotlights/*",
    (paths) => `${paths.conversion}/popups`,
  ),
  redirectExact(
    "conversion/onboard",
    (paths) => `${paths.conversion}/dashboard`,
  ),
  redirectExact("order-management", (paths) => paths.shipping),
  redirectWithTail(
    "order-management/warranties/*",
    (paths) => `${paths.returns}/warranties`,
  ),
  redirectWithTail(
    "order-management/returns/*",
    (paths) => `${paths.returns}/returns`,
  ),
  redirectWithTail(
    "order-management/claims/*",
    (paths) => `${paths.returns}/claims`,
  ),
  redirectWithTail(
    "order-management/registrations/*",
    (paths) => `${paths.returns}/registrations`,
  ),
  redirectWithTail(
    "order-management/recycle/*",
    (paths) => `${paths.returns}/recycle`,
  ),
  redirectWithTail(
    "order-management/withdrawals/*",
    (paths) => `${paths.returns}/withdrawals`,
  ),
  redirectExact(
    "order-management/order-edits",
    (paths) => `${paths.shipping}/outbound-labels/order-edits`,
  ),
  redirectExact(
    "order-management/analytics/order-fulfillment",
    (paths) => `${paths.shipping}/analytics/order-fulfillment`,
  ),
  redirectWithTail(
    "order-management/analytics/returns-claims/*",
    (paths) => `${paths.returns}/analytics/returns-claims`,
  ),
  redirectExact(
    "order-management/analytics/managed-returns",
    (paths) => `${paths.returns}/analytics/managed-returns`,
  ),
  redirectWithTail(
    "order-management/analytics/reports/*",
    (paths) => `${paths.returns}/analytics/reports`,
  ),
  redirectWithTail(
    "order-management/outbound-labels/packing-slips/*",
    packingSlips,
  ),
  redirectWithTail(
    "order-management/outbound-labels/*",
    (paths) => `${paths.shipping}/outbound-labels`,
  ),
  redirectWithTail(
    "order-management/wms/*",
    (paths) => `${paths.shipping}/wms`,
  ),
  redirectWithTail(
    "order-management/warehouse/*",
    (paths) => `${paths.shipping}/warehouse`,
  ),

  redirectWithTail(
    "approvals/*",
    (paths) => `${paths.orderManagement}/approvals`,
  ),
  redirectWithTail("warranties/*", (paths) => `${paths.returns}/warranties`),
  redirectWithTail("claims/*", (paths) => `${paths.returns}/claims`),
  redirectWithTail("recycle/*", (paths) => `${paths.returns}/recycle`),
  {
    from: "about-oms/*",
    to({ paths }) {
      return `${paths.shipping}/outbound-labels/about`;
    },
  },
  redirectWithTail(
    "returns/processing/*",
    (paths) => `${paths.shipping}/warehouse/processing`,
  ),
  ...legacyReturnListRedirects(),

  redirectWithTail("orders/outbound-labels/packing-slips/*", packingSlips),
  redirectWithTail(
    "orders/outbound-labels/*",
    (paths) => `${paths.shipping}/outbound-labels`,
  ),
  redirectExact(
    "orders/batch-history",
    (paths) => `${paths.shipping}/outbound-labels/batch-history`,
  ),
  redirectExact(
    "orders/bulk-fulfillments",
    (paths) => `${paths.shipping}/outbound-labels/bulk-fulfillments`,
  ),
  redirectExact(
    "orders/eod-manifest",
    (paths) => `${paths.shipping}/outbound-labels/eod-manifest`,
  ),
  redirectExact("orders/packing-slips", packingSlips),
  redirectExact(
    "orders/personalized-slips",
    (paths) => `${paths.shipping}/outbound-labels/personalized-slips`,
  ),
  {
    from: "orders/*",
    to({ paths, tail }) {
      if (!tail) {
        return paths.shipping;
      }
      return appendTail(`${paths.shipping}/outbound-labels/orders`, tail);
    },
  },

  redirectExact(
    "shipping/order-edits",
    (paths) => `${paths.shipping}/outbound-labels/order-edits`,
  ),
  redirectWithTail("shipping/outbound-labels/packing-slips/*", packingSlips),

  ...analyticsRedirects("home/analytics"),
  redirectWithTail(
    "reports/*",
    (paths) => `${paths.returns}/analytics/reports`,
  ),
  {
    from: "summary/*",
    to({ paths }) {
      return `${paths.home}/overview`;
    },
  },
  {
    from: "home/*",
    to({ paths }) {
      return `${paths.home}/overview`;
    },
  },

  redirectExact("support/about", (paths) => `${paths.supportV2}/about`),
  redirectExact("support/setup", (paths) => `${paths.supportV2}/setup`),
  redirectWithTail("support/faq/*", (paths) => `${paths.supportV2}/faq`),
  redirectWithTail("support/csat/*", (paths) => `${paths.supportV2}/csat`),
  ...[
    "support-v2/concierge/preview",
    "support/concierge/preview",
    "concierge/preview",
  ].map(
    (from): LegacyRedirect => ({
      from,
      to({ paths, search }) {
        search.delete("step");
        search.set("tab", "general");
        return `${paths.settings}/customer-widget`;
      },
    }),
  ),
  redirectWithTail(
    "support/concierge/*",
    (paths) => `${paths.supportV2}/concierge`,
  ),
  redirectWithTail("concierge/*", (paths) => `${paths.supportV2}/concierge`),
  redirectWithTail("redo-concierge/*", (paths) => paths.commerceAgent),

  redirectWithTail(
    "marketing/campaigns/*",
    (paths) => `${paths.marketing}/email-sms/campaigns`,
  ),
  redirectWithTail(
    "marketing/templates/*",
    (paths) => `${paths.marketing}/email-sms/templates`,
  ),
  redirectWithTail(
    "marketing/audience/*",
    (paths) => `${paths.marketing}/email-sms/audience`,
  ),
  redirectWithTail(
    "marketing/segments/*",
    (paths) => `${paths.marketing}/email-sms/segments`,
  ),
  redirectWithTail("reviews/*", (paths) => `${paths.marketing}/reviews`),
  redirectWithTail("tracking/*", (paths) => `${paths.marketing}/tracking`),
  redirectWithTail("gorgias/*", (paths) => `${paths.support}/gorgias`),

  redirectWithTail(
    "finance/disputes/dashboard/*",
    (paths) => `${paths.payments}/chargebacks/overview`,
  ),
  redirectWithTail(
    "finance/disputes/chargebacks/*",
    (paths) => `${paths.payments}/chargebacks/reclaim`,
  ),
  redirectWithTail(
    "finance/disputes/alerts/*",
    (paths) => `${paths.payments}/chargebacks/alerts`,
  ),
  redirectWithTail(
    "finance/disputes/resolve/*",
    (paths) => `${paths.payments}/chargebacks/resolve`,
  ),
  redirectWithTail(
    "finance/disputes/settings/*",
    (paths) => `${paths.settings}/chargebacks/reclaim`,
  ),
  redirectWithTail(
    "finance/disputes/integrations/*",
    (paths) => `${paths.settings}/integrations`,
  ),
  redirectExact("finance/disputes", (paths) => `${paths.payments}/chargebacks`),
  redirectWithTail(
    "finance/disputes/*",
    (paths) => `${paths.payments}/chargebacks/reclaim`,
  ),
  {
    from: "finance/*",
    to({ paths }) {
      return `${paths.payments}/chargebacks`;
    },
  },

  redirectExact("marketing/text-concierge", (paths) => paths.commerceAgent),
  redirectExact(
    "marketing/text-concierge/setup",
    (paths) => paths.commerceAgent,
  ),
  redirectExact(
    "marketing/text-concierge/settings",
    (paths) => `${paths.commerceAgent}/playbook`,
  ),
  ...["conversations", "analytics"].map((subPath) =>
    redirectExact(
      `marketing/text-concierge/${subPath}`,
      (paths) => `${paths.commerceAgent}/${subPath}`,
    ),
  ),

  redirectExact(
    "payments/chargebacks/settings",
    (paths) => `${paths.settings}/chargebacks/reclaim`,
  ),
  hubRedirect("payments/chargebacks/integrations", "reclaim"),
  {
    from: "payments/chargebacks/:disputeId",
    to: ({ paths, params }) =>
      `${paths.payments}/chargebacks/reclaim/${segment(params, "disputeId")}`,
  },

  redirectExact(
    "settings/conversion/hsa",
    (paths) => `${paths.settings}/payments/hsa`,
  ),
  ...["try-first", "try-first/*"].map((from) =>
    redirectExact(
      `settings/${from}`,
      (paths) => `${paths.settings}/conversion/try-first`,
    ),
  ),
  ...["checkout-optimization", "checkout-optimization/*"].map((from) =>
    redirectExact(
      `settings/${from}`,
      (paths) => `${paths.settings}/conversion/general`,
    ),
  ),
  redirectExact(
    "settings/exchanges/guidance",
    (paths) => `${paths.settings}/customer-portal/guidance`,
  ),
  redirectExact(
    "settings/sort-destinations",
    (paths) => `${paths.settings}/post-processing`,
  ),
  redirectExact(
    "settings/return-portal",
    (paths) => `${paths.settings}/customer-portal`,
  ),
  redirectExact(
    "settings/invoices",
    (paths) => `${paths.settings}/billing/bills`,
  ),
  ...["subscription", "forecasting"].map((from) =>
    redirectExact(
      `settings/billing/ims/${from}`,
      (paths) => `${paths.settings}/billing/ims`,
    ),
  ),
  redirectExact(
    "settings/parcels",
    (paths) => `${paths.settings}/oms/packages`,
  ),
  redirectExact(
    "settings/balance",
    (paths) => `${paths.settings}/billing/outbound`,
  ),
  redirectExact("settings/locations", (paths) => `${paths.settings}/shipping`),
  redirectExact(
    "settings/oms/balance",
    (paths) => `${paths.settings}/billing/outbound`,
  ),
  redirectExact(
    "settings/order-management/returns-centers",
    (paths) => `${paths.settings}/warehouse`,
  ),
  redirectExact(
    "settings/order-management/returns-centers/:centerId",
    (paths) => `${paths.settings}/warehouse`,
  ),
  redirectExact(
    "settings/chargebacks/general",
    (paths) => `${paths.settings}/chargebacks/reclaim`,
  ),
  redirectExact(
    "settings/unified-integrations",
    (paths) => `${paths.settings}/integrations`,
  ),
  {
    from: "settings/unified-integrations/vendor/:slug",
    to: ({ paths, params }) =>
      `${paths.settings}/integrations/vendor/${segment(params, "slug")}`,
  },
  hubRedirect("settings/customer-accounts-integrations"),
  hubRedirect("settings/order-tracking-integrations"),
  hubRedirect("settings/oms/integrations"),
  hubRedirect("settings/chargebacks/integrations", "reclaim"),
  ...legacyIntegrationVendorRedirects(),
  {
    from: "settings/customer-satisfaction",
    to: ({ paths, search }) => `${paths.supportV2}/csat/${csatSubpage(search)}`,
  },
  redirectExact("settings/faq", (paths) => `${paths.faq}/home`),
  redirectExact("integrations", (paths) => `${paths.settings}/integrations`),
  redirectExact("support-v2/settings", (paths) => `${paths.settings}/general`),
  redirectExact(
    "commerce-agent/settings",
    (paths) => `${paths.commerceAgent}/playbook`,
  ),
  redirectWithTail("inventory/catalog/*", (paths) => paths.catalog),
  redirectExact("inventory/variants", (paths) => `${paths.catalog}/variants`),
  redirectWithTail(
    "inventory/bill-of-materials/*",
    (paths) => `${paths.catalog}/bill-of-materials`,
  ),
];

function storeBaseFromParams(params: Params): string {
  const teamId = params.teamId;
  if (!teamId) {
    return "";
  }
  return `/stores/${encodeURIComponent(teamId)}`;
}

function redirectHref(
  entry: RedirectTarget,
  params: Params,
  rawSearch: string,
  pathname: string,
  tail: string,
): string {
  const search = new URLSearchParams(rawSearch);
  const original = new URLSearchParams(rawSearch);
  const targetPath = entry.to({
    params,
    search,
    paths: storePaths(storeBaseFromParams(params)),
    pathname,
    tail,
  });
  if (original.toString() === search.toString()) {
    return rawSearch ? `${targetPath}${rawSearch}` : targetPath;
  }
  const query = search.toString();
  return query ? `${targetPath}?${query}` : targetPath;
}

const redirectByRoute = new Map<RouteObject, RedirectTarget>();

const loggedMessages = new Set<string>();

function logOnce(message: string, source: string, target: string): void {
  const key = `${message} ${source} ${target}`;
  if (loggedMessages.has(key)) {
    return;
  }
  loggedMessages.add(key);
  datadogLogs.logger.error(message, { source, target });
}

export type LegacyRedirectDecision =
  | { action: "redirect"; href: string }
  | { action: "not-found" };

export function legacyRedirectResponse(
  decision: LegacyRedirectDecision | null,
): Response {
  if (decision?.action === "redirect") {
    return redirect(decision.href);
  }
  throw new Response(null, { status: 404, statusText: "Not Found" });
}

let getRootRoutes: (() => RouteObject[]) | undefined;

/** The one loader behind every redirect route. Its hrefs are basename-free: the router adds the basename. */
const redirectLoader: LoaderFunction = ({ request }) => {
  if (!getRootRoutes) {
    throw new Error(
      "A redirect route ran before legacyRedirectRoutes registered the root route tree",
    );
  }
  return legacyRedirectResponse(
    resolveScopedRedirect(
      getRootRoutes(),
      request.url,
      getSessionAuth,
      routerBasename,
    ),
  );
};

/** Compiles the table into loader routes and binds the root tree every redirect loader resolves against. */
export function legacyRedirectRoutes(
  table: readonly LegacyRedirect[],
  getRoutes: () => RouteObject[],
): PermissionedRouteObject[] {
  getRootRoutes = getRoutes;
  return table.map((entry) => {
    const route: PermissionedRouteObject = {
      path: entry.from,
      loader: redirectLoader,
    };
    redirectByRoute.set(route, entry);
    return route;
  });
}

/**
 * An index route that redirects. A root table entry for the same path would tie
 * with the live parent route and lose to it, so this is declared on the parent.
 */
export function indexRedirectRoute(
  to: RedirectTarget["to"],
): PermissionedRouteObject {
  const route: PermissionedRouteObject = {
    index: true,
    loader: redirectLoader,
  };
  redirectByRoute.set(route, { to });
  return route;
}

/** Index route that forwards the parent's own URL to one of its children. */
export function defaultChildRoute(child: string): PermissionedRouteObject {
  return indexRedirectRoute(
    ({ pathname }) => `${pathname.replace(/\/+$/, "")}/${child}`,
  );
}

interface RedirectHop {
  /** Router-space source, matched pathname plus query. */
  source: string;
  /** Router-space target. */
  href: string;
  isIndex: boolean;
}

function routerSpacePath(
  rawPathname: string,
  basename: string | undefined,
): string {
  const prefix = basename?.replace(/\/+$/, "") ?? "";
  return prefix && rawPathname.toLowerCase().startsWith(prefix.toLowerCase())
    ? rawPathname.slice(prefix.length)
    : rawPathname;
}

/**
 * Splits the raw request path after `matchedSegments` segments. The router
 * decodes what it matches, so the tail is cut from the raw path instead and
 * keeps its case and encoding. Empty segments are dropped, as the router does,
 * and a trailing slash on a non-empty tail is kept once.
 */
function splitRawPath(
  rawPathname: string,
  basename: string | undefined,
  matchedSegments: number,
): { pathname: string; tail: string } {
  const routerPath = routerSpacePath(rawPathname, basename);
  const segments = routerPath.split("/").filter(Boolean);
  const tail = segments.slice(matchedSegments).join("/");
  return {
    pathname: `/${segments.slice(0, matchedSegments).join("/")}`,
    tail: tail && routerPath.endsWith("/") ? `${tail}/` : tail,
  };
}

/** `href` is router-space when `basename` is undefined; otherwise it must start with the basename. */
function redirectHop(
  routes: RouteObject[],
  href: string,
  basename: string | undefined,
): RedirectHop | null {
  const url = new URL(href, "http://localhost");
  const matches = matchRoutes(routes, url.pathname, basename ?? "/");
  const leaf = matches?.[matches.length - 1];
  const entry = leaf && redirectByRoute.get(leaf.route);
  if (!leaf || !entry) {
    return null;
  }
  const matchedSegments = leaf.pathnameBase.split("/").filter(Boolean).length;
  const { pathname, tail } = splitRawPath(
    url.pathname,
    basename,
    matchedSegments,
  );
  return {
    source: `${pathname}${url.search}`,
    href: redirectHref(entry, leaf.params, url.search, pathname, tail),
    isIndex: leaf.route.index === true,
  };
}

const MAX_REDIRECT_HOPS = 8;

/**
 * Follows every redirect hop to the final router-space URL, or not-found on a
 * cycle. Null when `href` is not a redirect. Index-to-index hops are expected
 * (`/inventory/catalog` -> `.../products` -> `.../products/active`). A table
 * entry after the first hop is a chain: it still resolves, but is logged, and
 * the table spec keeps every table entry to one hop.
 */
export function resolveLegacyRedirect(
  routes: RouteObject[],
  href: string,
  basename: string | undefined = routerBasename,
): LegacyRedirectDecision | null {
  const first = redirectHop(routes, href, basename);
  if (!first) {
    return null;
  }
  const seen = new Set([first.source, first.href]);
  let current = first;
  for (let hops = 1; hops < MAX_REDIRECT_HOPS; hops++) {
    const next = redirectHop(routes, current.href, undefined);
    if (!next) {
      return { action: "redirect", href: current.href };
    }
    if (!next.isIndex) {
      logOnce("Legacy redirect chain", first.source, next.href);
    }
    if (seen.has(next.href)) {
      break;
    }
    seen.add(next.href);
    current = next;
  }
  logOnce("Legacy redirect cycle", first.source, current.href);
  return { action: "not-found" };
}

/**
 * Like `resolveLegacyRedirect`, but a redirect from an unscoped URL lands in the
 * stored auth's team, as base's redirect re-render scoped it. Scoped and
 * non-redirect URLs are left alone. The one place that applies this rule, for
 * the pre-router step and the loaders alike.
 */
export function resolveScopedRedirect(
  routes: RouteObject[],
  href: string,
  readAuth: (routerPath: string) => { teamId: string } | undefined,
  basename: string | undefined = routerBasename,
): LegacyRedirectDecision | null {
  const decision = resolveLegacyRedirect(routes, href, basename);
  if (decision?.action !== "redirect") {
    return decision;
  }
  const original = routerSpacePath(new URL(href).pathname, basename);
  const teamId = original.startsWith("/stores/")
    ? undefined
    : readAuth(original)?.teamId;
  return teamId
    ? { action: "redirect", href: getStoreScopedPath(teamId, decision.href) }
    : decision;
}

/** The URL a first load is rewritten to before the router starts, with the basename, or null when it is not a redirect. */
export function firstLoadRedirectHref(
  routes: RouteObject[],
  href: string,
  readAuth: (routerPath: string) => { teamId: string } | undefined,
  basename: string | undefined = routerBasename,
): string | null {
  const decision = resolveScopedRedirect(routes, href, readAuth, basename);
  if (decision?.action !== "redirect") {
    return null;
  }
  return `${basename?.replace(/\/+$/, "") ?? ""}${decision.href}`;
}

/**
 * Rewrites the legacy URL the browser is on to its target, in place, keeping
 * `history.state` (the router's index and key). The hash is dropped because
 * first loads with a hash render blank until the react-router upgrade.
 */
export function replaceLegacyLocation(
  routes: RouteObject[],
  readAuth: (routerPath: string) => { teamId: string } | undefined,
  win: Window = window,
): void {
  const href = firstLoadRedirectHref(routes, win.location.href, readAuth);
  if (href) {
    win.history.replaceState(win.history.state, "", href);
  }
}
